import React, { useState } from 'react';
import { UserProfileCard } from '../molecues/UserProfileCard';
import { SidebarSection } from '../molecues/SidebarSection';
import type { SidebarItemProps } from '../molecues/SidebarItem';
import { useAuthContext } from '../../contexts/AuthContext';
import logo from '../../assets/saung-logo.svg';

/**
 * Sidebar Component - Organism Level
 * 
 * Main navigation sidebar with collapsible functionality
 * 
 * Features:
 * - Collapsible/expandable
 * - Gradient background (theme colors)
 * - User profile card
 * - Nested navigation
 * - Role-based menu filtering
 * - Responsive design
 */

export interface SidebarProps {
  /** Navigation sections */
  sections: Array<{
    title?: string;
    items: SidebarItemProps[];
  }>;
  /** Whether sidebar is initially collapsed */
  defaultCollapsed?: boolean;
  /** Callback when collapse state changes */
  onCollapseChange?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  sections,
  defaultCollapsed = false,
  onCollapseChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { user } = useAuthContext();

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapseChange?.(newState);
  };

  // Handle different user profile types
  const getUserName = (user: unknown) => {
    if (!user || typeof user !== 'object') return 'Guest User';
    
    // Check if it's a User type (has firstName/lastName)
    if ('firstName' in user && 'lastName' in user) {
      const userObj = user as { firstName?: string; lastName?: string; fullName?: string };
      return `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || userObj.fullName || 'User';
    }
    
    // For MerchantProfile or AdminProfile, use fullName
    const userObj = user as { username?: string };
    return userObj.username || 'User';
  };

  const getUserRole = (user: unknown) => {
    if (!user || typeof user !== 'object') return 'Guest';
    
    // Check if it's a User type (has role)
    if ('role' in user) {
      const userObj = user as { role?: string };
      return userObj.role;
    }
    
    // For AdminProfile, use accessLevel
    if ('accessLevel' in user) {
      const userObj = user as { accessLevel?: string };
      return userObj.accessLevel;
    }
    
    // For MerchantProfile, default to MERCHANT
    return 'MERCHANT';
  };

  const getAvatarUrl = (user: unknown) => {
    if (!user || typeof user !== 'object') return undefined;
    
    // Check if it's a User type (has avatar)
    if ('avatar' in user) {
      const userObj = user as { avatar?: string };
      return userObj.avatar;
    }
    
    // For other profile types, no avatar available
    return undefined;
  };

  const userName = getUserName(user);
  const userRole = getUserRole(user);
  const avatarUrl = getAvatarUrl(user);

  return (
    <aside
      className={`
        fixed left-4 top-4 bottom-4
        bg-gradient-to-b from-[#0B0B0F] via-[#121218] to-[#713F12]
        shadow-2xl transition-all duration-300 z-40
        rounded-lg overflow-hidden
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}
      aria-label="Main navigation"
    >
      <div className="flex flex-col h-full rounded-2xl">
        {/* Header with Logo */}
        <div className={`flex items-center justify-between p-4 border-b border-white/10 ${isCollapsed ? 'justify-center' : ''}`}>
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <img src={logo} alt="SAUNG" className="h-8 w-auto" />
              <div>
                <h1 className="!text-white !font-bold !text-lg">SAUNG</h1>
                <p className="text-xs pl-4 text-gray-400">Admin Dashboard</p>
              </div>
            </div>
          )}
          
          {isCollapsed && (
            <img src={logo} alt="SAUNG" className="h-8 w-auto" />
          )}
        </div>

        {/* User Profile Card */}
        <div className={`p-4 border-b border-white/10 ${isCollapsed ? 'px-2' : ''}`}>
          <UserProfileCard
            name={userName}
            role={userRole}
            avatarUrl={avatarUrl}
            status="online"
            isCollapsed={isCollapsed}
          />
        </div>

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          {sections.map((section, index) => (
            <SidebarSection
              key={section.title || index}
              title={section.title}
              items={section.items}
              isCollapsed={isCollapsed}
            />
          ))}
        </nav>

        {/* Footer with Collapse Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleToggleCollapse}
            className={`
              w-full flex items-center gap-3 py-2 px-3 rounded-lg
              text-gray-300 hover:bg-white/10 hover:text-white
              transition-all duration-200
              ${isCollapsed ? 'justify-center' : 'justify-between'}
            `}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {!isCollapsed && <span className="text-sm font-medium">Collapse</span>}
            
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${
                isCollapsed ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>
        </div>
      </div>

    </aside>
  );
};

export default Sidebar;

